// Helper: Merge with existing shapes and update selection state
// Helper: Assign order and coordinates to a shape

import { getNextChartOrder } from '@/utils/chartHandlers';
import { shapeToCoordinates } from '@/utils/coordinateConverter';
import { cancelDrawing } from './KeyEvents';
import { entries, type ChartId } from '..';
import type { YCoordinateClickedData } from '../contexts/ChartUIContext';
import type {
  ShapeSelection,
  RenderNode,
  RFCInteractiveItem,
  OrderLineProps,
  RenderNodesMap,
  TransientBroadcastDragOverride,
} from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import type { DrawingSharedState } from '../types/commontypes';

// Helper: Get the newly drawn shape
export const getOnlyNewShape = (newShapes: RenderNode[]) => {
  const onlyNewShape = newShapes[newShapes.length - 1];
  return onlyNewShape;
};

import type { XAccessor } from '@/hooks/use-candle-data';
import type { StockData } from '../types/chart';

// Helper: Assign order and coordinates to a shape
export const prepareNewShape = <T extends RenderNode>(
  shape: T,
  renderNodes: RenderNodesMap,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
  type?: ShapeType,
): T => {
  const orderChartId = shape.chartId;
  const nextOrder = getNextChartOrder(renderNodes, orderChartId);
  // spread로 인한 union 타입 소실을 함수 내부 1곳에서 처리

  return {
    ...shape,
    // RFC 래퍼에서 shapeType을 설정하지 않은 경우, 인자로 전달된 shapeType으로 보완
    type: shape.type || type,
    order: shape.order || nextOrder,
    coordinates: shapeToCoordinates(shape, candleData, timeframe, xAccessor),
  } satisfies T;
};

// Helper: Handle UI and drawing states
export const resetDrawingState = (
  _shapeType: ShapeType,
  setShapeTypeToDraw: (value: ShapeType | undefined) => void,
  setEnableShapeSelection: (value: boolean) => void,
  setYCoordinateClicked: React.Dispatch<React.SetStateAction<YCoordinateClickedData | undefined>>,
  setOverrideData: (overrideData: TransientBroadcastDragOverride) => void,
  setDrawingSharedState: React.Dispatch<React.SetStateAction<DrawingSharedState>>,
) => {
  setShapeTypeToDraw(undefined);
  setEnableShapeSelection(false);
  setYCoordinateClicked(undefined);
  setOverrideData({});
  setDrawingSharedState({});
};

//Disable states on cancel drawing event
export const disableStateOnCancel = (
  chartId: ChartId,
  chartCanvasRef: React.RefObject<{ cancelDrag?: () => void } | null>,
  shapeRefs: React.RefObject<Record<string, unknown> | null>,
  drawState: DrawingSharedState,
  setDrawingSharedState: React.Dispatch<React.SetStateAction<DrawingSharedState>>,
  shapeTypeToDraw: ShapeType | undefined,
  setShapeTypeToDraw: (value: ShapeType | undefined) => void,
) => {
  cancelDrawing(shapeTypeToDraw, chartCanvasRef, shapeRefs);
  setDrawingSharedState({});
  if (drawState.chartId === chartId) {
    setShapeTypeToDraw(undefined);
  }
};

// Helper: Handle alert for dragged order line shapes
export const handleDraggedOrderLine = (
  newShapes: RenderNode[],
  dragged?: boolean,
  setAlertToEdit?: (alert: Partial<OrderLineProps>) => void,
) => {
  if (!dragged || newShapes.length === 0) return;

  // RFC callback data contains selected flag as RFCInteractiveItem, not RenderNode
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const selectedItem = newShapes.find(item => (item as unknown as RFCInteractiveItem).selected === true);
  if (!selectedItem) return;

  // 외부 차트 라이브러리(react-financial-charts)의 드래그 콜백이 돌려주는 객체에는
  // amount, tpslPrice, text 등의 필드가 런타임에 포함되어 있지만,
  // 우리 타입(RenderNode)에는 이 필드들이 정의되어 있지 않다.
  // RFCInteractiveItem은 [key: string]: unknown 인덱스 시그니처를 가지고 있어서
  // 이 런타임 필드들에 접근할 수 있게 해주는 느슨한 타입이다.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const rfcItem = selectedItem as unknown as RFCInteractiveItem;

  const yValue = selectedItem.type === ShapeType.ORDER_LINE ? selectedItem.yValue : undefined;

  const updatedAlert: Partial<OrderLineProps> = {
    amount: Number(rfcItem['amount']),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    positionType: rfcItem['text'] as 'long' | 'short',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    tpsl: rfcItem['tpsl'] as Array<{ tp: number; sl: number }> | undefined,
    price: yValue,
    type: ShapeType.ORDER_LINE,
  };

  setAlertToEdit?.(updatedAlert);
};

/**
 * Replaces the shapes for a given chart and type with newShapes
 * This assumes newShapes are all valid existing shapes to update
 */
export const createRenderNodesMap = <T extends RenderNode>(
  renderNodes: RenderNodesMap,
  newShapes: T[],
  type: ShapeType,
): RenderNodesMap => {
  if (!newShapes || newShapes.length === 0) return renderNodes;

  const chartId = newShapes[0].chartId; // assume all shapes belong to same chart

  // Make a shallow copy of renderNodes
  const updatedShapesData: RenderNodesMap = { ...renderNodes };

  if (!updatedShapesData[chartId]) updatedShapesData[chartId] = {};

  // Replace shapes array for this chart and type
  // RenderNode[]를 특정 variant 배열에 할당 — 런타임에 shapeType별 올바른 도형만 포함됨
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  (updatedShapesData[chartId] as Record<ShapeType, RenderNode[]>)[type] = newShapes;

  return updatedShapesData;
};

//Update coordinates based on new data points
// Note: selectedUuid identifies the dragged shape — previously read from shape.selected (RFC callback artifact)
export const updateCoordinate = <T extends RenderNode>(
  shapesList: T[],
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
  selectedUuid?: string,
): T[] => {
  // Only update the coordinates for the selected shape(s)
  return shapesList.map(shape => {
    if (shape.uuid === selectedUuid) {
      return {
        ...shape,
        coordinates: shapeToCoordinates(shape, candleData, timeframe, xAccessor),
      } satisfies T;
    }
    return shape;
  });
};

/**
 * Sets the globally selected shape based on library callback data.
 * No longer mutates renderNodes — selection is managed solely via selectedShape.
 *
 * @param chartId - Chart ID where the shape was selected
 * @param type - Shape type category
 * @param callbackShapes - Shapes array from library callback (contains selected flags)
 * @param timeframe - Current timeframe
 * @param setSelectedShape - Global selection setter
 */
export const handleSelectedStatus = <T extends RenderNode>(
  chartId: ChartId,
  type: ShapeType,
  callbackShapes: T[],
  timeframe: string,
  setSelectedShape: (data: ShapeSelection) => void,
): void => {
  // selected flag lives on RFCInteractiveItem (RFC callback data), not on RenderNode
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const sel = callbackShapes.find(s => (s as unknown as RFCInteractiveItem).selected);
  if (!sel) return;
  const mode = sel.createdAt !== sel.updatedAt ? 'edit' : 'create';
  setSelectedShape({ chartId, uuid: sel.uuid, timeframe, type: type, mode });
};

export const emptyMeasureArray = (renderNodes: RenderNodesMap): RenderNodesMap => {
  // Measure가 있는 chartId만 새 참조를 생성하여, per-chartId 셀렉터가 불필요한 리렌더를 방지할 수 있도록 한다.
  let hasMeasures = false;
  for (const [, chartShapes] of entries(renderNodes || {})) {
    const measures = chartShapes[ShapeType.MEASURE];
    if (measures && measures.length > 0) {
      hasMeasures = true;
      break;
    }
  }

  // Return same reference if nothing to clear — prevents unnecessary setRenderNodes calls
  if (!hasMeasures) return renderNodes;

  const result: RenderNodesMap = { ...renderNodes };
  for (const [cid, chartShapes] of entries(renderNodes || {})) {
    const measures = chartShapes[ShapeType.MEASURE];
    if (measures && measures.length > 0) {
      result[cid] = { ...chartShapes, [ShapeType.MEASURE]: [] };
    }
    // Measure가 없는 chartId는 원래 참조를 유지
  }
  return result;
};
